/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { ICoreContext } from '@msdyn365-commerce/core-internal';
import { ProductDimension, ProductDimensionType } from '@msdyn365-commerce/retail-proxy';
import {
    ArrayExtensions,
    convertProductDimensionTypeToDimensionTypes,
    DimensionTypes,
    getDimensionValuesFromQuery,
    getInventoryLevelCodeFromDimensionValue,
    IDimensionForSelectedVariant,
    IDimensionsApp,
    IDimensionValueForSelectedVariant,
    InventoryLevelValues,
    ObjectExtensions
} from '@msdyn365-commerce-modules/retail-actions';
import { format, getTelemetryObject, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import {
    IBuyboxCallbacks,
    IBuyboxCommonData,
    IBuyboxCommonResources,
    IBuyboxExtentedProps,
    IBuyboxProductConfigureDropdownViewProps,
    IBuyboxProductConfigureViewProps,
    IBuyboxState,
    IProductDetails
} from '@msdyn365-commerce-modules/buybox';
// eslint-disable-next-line no-duplicate-imports
import { Dropdown, IDropdownItemProps, IDropdownOnSelectionChangeNotification, SelectionType } from '@msdyn365-commerce-modules/buybox';
import { RadioButtonList, IDropdownExtOnSelectionChangeNotification } from './radio-button-list';

let telemetryContent: ITelemetryContent;

/**
 * Dimension pre-processing info.
 */
interface IProductDimensionInfo {
    dropdownName: string;
    dropdownId: string;
    dropdownList: IDropdownItemProps[];
}

/**
 * Props for rendering a dropdown.
 */
interface IProductDimensionDropdownProps extends IProductDimensionInfo {
    resources: IBuyboxCommonResources;
    className: string;
    context: ICoreContext<IDimensionsApp>;

    configureErrors: { [configureId: string]: string | undefined };

    getDropdownName(dimensionType: number, resources: IBuyboxCommonResources): string;
    dropdownCallback(notification: IDropdownOnSelectionChangeNotification): Promise<void>;

    defaultFirstOption: boolean;
}

const mapProductDimensionFullToDropdownViewProps = (props: IProductDimensionDropdownProps): IBuyboxProductConfigureDropdownViewProps => {
    const dimensionId = Number.parseInt(props.dropdownId, 10) as ProductDimensionType;
    const dropdownToggleName = format(props.resources.selectDimensionFormatString, props.dropdownName);

    const errorMessage = props.configureErrors[props.dropdownId];

    let dimensionSelectionType = SelectionType.dropdown;

    const dimensionType = convertProductDimensionTypeToDimensionTypes(dimensionId);

    const dimensionsAsSwatchConfiguration = props.context.app.config.dimensionsAsSwatchType;
    if (
        ArrayExtensions.hasElements(dimensionsAsSwatchConfiguration) &&
        !dimensionsAsSwatchConfiguration.includes(DimensionTypes.none) &&
        dimensionsAsSwatchConfiguration.includes(dimensionType)
    ) {
        dimensionSelectionType = SelectionType.swatch;
    }

    const isHidden =
        props.defaultFirstOption &&
        dimensionType === DimensionTypes.size &&
        props.dropdownList.length === 1 &&
        props.dropdownList[0].value?.toLocaleLowerCase() === 'n/a';
    const hiddenClass = isHidden ? 'd-none' : '';

    return {
        ContainerProps: {
            className: `${props.className}__dropdown ${hiddenClass}`
        },
        LabelContainerProps: {
            tag: 'label',
            className: `${props.className}__dropdown-quantity-label`,
            htmlFor: `${props.className}__dropown-quantity-input-${props.dropdownId}`
        },
        errors: errorMessage && (
            <span className='msc-alert msc-alert-noborder msc-alert-danger' role='alert' aria-live='assertive'>
                <span className='msi-exclamation-triangle' aria-hidden='true' />
                <span>{errorMessage}</span>
            </span>
        ),
        heading: <div>{props.dropdownName}</div>,
        select:
            dimensionSelectionType === SelectionType.swatch ? (
                <RadioButtonList
                    dropdownId={props.dropdownId}
                    dropdownName={props.dropdownName}
                    dropdownToggleName={dropdownToggleName}
                    dropdownList={props.dropdownList}
                    onChange={props.dropdownCallback}
                    telemetryContent={telemetryContent}
                    defaultFirstOption={props.defaultFirstOption}
                />
            ) : (
                <Dropdown
                    dropdownId={props.dropdownId}
                    dropdownName={props.dropdownName}
                    dropdownToggleName={dropdownToggleName}
                    dropdownList={props.dropdownList}
                    onChange={props.dropdownCallback}
                    telemetryContent={telemetryContent}
                    selectionType={dimensionSelectionType}
                    apiSettings={props.context.request.apiSettings}
                />
            )
    };
};

export function getBuyboxProductConfigure(
    props: IBuyboxExtentedProps<IBuyboxCommonData>,
    state: IBuyboxState,
    callbacks: IBuyboxCallbacks,
    productDetails?: IProductDetails,
    selectedDimensions?: ProductDimension[],
    defaultFirstOption?: boolean,
    allDimensions?: IDimensionForSelectedVariant[] | null
): IBuyboxProductConfigureViewProps | undefined {
    const { resources, typeName } = props;

    const {
        errorState: { configureErrors }
    } = state;

    const { getDropdownName } = callbacks;

    const context = props.context as ICoreContext<IDimensionsApp>;

    // Since the data action can return no value, and the type is non-nullable due to backward compatibility reasons, the null check is required.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Explicitly check for null/undefined.
    const product = props.data.product?.result ? props.data.product.result : productDetails?.product;

    // Since the data action can return no value, and the type is non-nullable due to backward compatibility reasons, the null check is required.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Explicitly check for null/undefined.
    const productDimensionsData = props.data.productDimensions?.result ?? productDetails?.productDimensions;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access -- app context is generic
    const enableStockCheck = props.context.app.config.enableStockCheck;

    const hasProductDimensions = ArrayExtensions.hasElements(productDimensionsData);

    if (!product || !hasProductDimensions) {
        return undefined;
    }

    const className = _getClassNamePrefix(typeName);

    telemetryContent = getTelemetryObject(context.request.telemetryPageName!, props.typeName, props.telemetry);

    const inventoryLevel = context.app.config.inventoryLevel;

    let dimensionsInfo: IProductDimensionInfo[] = [];

    const productDimensions = allDimensions || (productDimensionsData as IDimensionForSelectedVariant[]);

    const onChanged = async (notification: IDropdownExtOnSelectionChangeNotification) => {
        // user selected an options that's not available with other selected options
        const otherDimensions: { [id: number]: string } = {};
        if (!notification.isValid) {
            process.env.NODE_ENV === 'development' && console.log('buybox-product-configure', 'invalid option selected', notification);
            const productDimension = productDimensions.find(pd => pd.DimensionTypeValue.toString() === notification.dropdownId);
            const dimensionValuesWithInventory = productDimension!.dimensionValuesWithInventory!.find(
                dv => dv.DimensionValue?.Value === notification.selectedValue
            );

            if (dimensionValuesWithInventory) {
                const targetProductId = dimensionValuesWithInventory.ProductIds![0];

                for (const productDimensionFull of productDimensions) {
                    if (productDimensionFull.DimensionTypeValue.toString() !== notification.dropdownId) {
                        for (const dv of productDimensionFull.dimensionValuesWithInventory || []) {
                            if (dv.ProductIds?.find(id => id === targetProductId)) {
                                process.env.NODE_ENV === 'development' &&
                                    console.log('buybox-product-configure', 'update option', dv.DimensionValue?.Value?.toString());

                                otherDimensions[dv.DimensionTypeValue!] = dv.DimensionValue!.RecordId.toString();
                            }
                        }
                    }
                }
            }
        }

        await _onChanged(notification, callbacks.dimensionSelectedAsync, otherDimensions);
    };

    const matchedDimensions = selectedDimensions ?? getDimensionValuesFromQuery(context.request.url.requestUrl);

    const validDimensionIds = {};
    for (const productDimensionFull of productDimensions) {
        const selectedOtherDimensions = matchedDimensions.filter(md => md.DimensionTypeValue !== productDimensionFull.DimensionTypeValue);

        const dimensionValuesWithInventory = productDimensionFull.dimensionValuesWithInventory;
        if (dimensionValuesWithInventory) {
            for (const dv of dimensionValuesWithInventory) {
                let matchFound = selectedOtherDimensions.length === 0; // allow any option when none is selected
                if (!matchFound) {
                    const productIds = dv.ProductIds || [];
                    for (const productId of productIds) {
                        if (!matchFound) {
                            for (const currentDimension of selectedOtherDimensions) {
                                if (!matchFound) {
                                    const currentDimensionInv =
                                        productDimensions.find(pd => pd.DimensionTypeValue === currentDimension.DimensionTypeValue)
                                            ?.dimensionValuesWithInventory || [];
                                    const cdi = currentDimensionInv.find(
                                        v =>
                                            v.DimensionValue?.Value?.toLowerCase() === currentDimension.DimensionValue?.Value?.toLowerCase()
                                    );

                                    if ((cdi?.ProductIds || []).find(val => val === productId)) {
                                        matchFound = true;

                                        break;
                                    }
                                }
                            }
                        }
                    }
                }

                if (matchFound && dv.DimensionValue) {
                    validDimensionIds[dv.DimensionValue.RecordId] = true;
                }
            }
        }
    }

    dimensionsInfo = productDimensions.map(productDimensionFull => {
        const dropdownName = getDropdownName(productDimensionFull.DimensionTypeValue, resources);
        const dropdownId = productDimensionFull.DimensionTypeValue.toString();

        const dimensions = (productDimensionFull.dimensionValuesWithInventory ??
            productDimensionFull.DimensionValues ??
            []) as IDimensionValueForSelectedVariant[];
        const validProductDimensionsFull = dimensions.filter(
            value => !ObjectExtensions.isNullOrUndefined(value.DimensionValue?.RecordId ?? value.RecordId)
        );

        const dropdownList: IDropdownItemProps[] = validProductDimensionsFull.map<IDropdownItemProps>(dimensionValuesWithInventory => {
            const dimensionValue = dimensionValuesWithInventory.DimensionValue ?? dimensionValuesWithInventory;
            const inventoryLevelCode = getInventoryLevelCodeFromDimensionValue(dimensionValuesWithInventory, inventoryLevel);

            const isValid = !!validDimensionIds[dimensionValue.RecordId];
            const item = {
                value: dimensionValue.Value ?? '',
                id: dimensionValue.RecordId.toString(),
                colorHexCode: dimensionValue.ColorHexCode,
                imageUrl: dimensionValue.ImageUrl,
                isValid: isValid,
                isDisabled: enableStockCheck && inventoryLevelCode === InventoryLevelValues.outOfStock
            };

            process.env.NODE_ENV === 'development' && !isValid && console.log('buybox-product-configure', 'dropdownList', item);

            return item;
        });

        return {
            dropdownName,
            dropdownId,
            dropdownList
        };
    });

    for (const dimensionInfo of dimensionsInfo) {
        const currentDimensionsFromQuery = matchedDimensions.filter(
            dimensionFromQuery => dimensionFromQuery.DimensionTypeValue.toString() === dimensionInfo.dropdownId
        );
        if (!ArrayExtensions.hasElements(currentDimensionsFromQuery)) {
            continue;
        }
        const currentDimensionFromQuery = currentDimensionsFromQuery[0];
        const selectedDimensionItem = dimensionInfo.dropdownList.filter(
            dimensionItem =>
                dimensionItem.value.toLocaleLowerCase() === currentDimensionFromQuery.DimensionValue?.Value?.toLocaleLowerCase()
        );
        if (!ArrayExtensions.hasElements(selectedDimensionItem)) {
            continue;
        }
        selectedDimensionItem[0].isDefault = true;
    }

    const dropdowns: IBuyboxProductConfigureDropdownViewProps[] = dimensionsInfo.map(dimensionInfo => {
        return mapProductDimensionFullToDropdownViewProps({
            ...dimensionInfo,
            resources,
            configureErrors,
            getDropdownName,
            dropdownCallback: onChanged,
            defaultFirstOption: defaultFirstOption || false,
            className,
            context
        });
    });

    return {
        ContainerProps: {
            className: `${className}__configure`
        },
        dropdowns
    };
}

const _onChanged = async (
    notification: IDropdownOnSelectionChangeNotification,
    dimensionChanged: (newValue: number, selectedDimensionValue: string, otherDimensions?: { [id: number]: string }) => Promise<void>,
    otherDimensions?: { [id: number]: string }
): Promise<void> => {
    await dimensionChanged(+notification.dropdownId, notification.selectId, otherDimensions);
};

const _getClassNamePrefix = (typeName: string): string => {
    return typeName.toLocaleLowerCase() === 'quickview' ? 'ms-quickView' : 'ms-buybox';
};
